// frontend/src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('app_token') || null);
  const [loading, setLoading] = useState(true);

  // Function to log in: stores token and user data
  const login = (userData, authToken) => {
    localStorage.setItem('app_token', authToken);
    setToken(authToken);
    setCurrentUser(userData);
    // console.log("[AuthContext] User logged in:", userData);
    // console.log("[AuthContext] Token set:", authToken);
  };

  // Function to log out: clears token and user data
  const logout = () => {
    localStorage.removeItem('app_token');
    setToken(null);
    setCurrentUser(null);
    // Potentially add Firebase sign out here if needed: auth.signOut();
    console.log("[AuthContext] User logged out.");
  };

  // Effect to verify token and fetch user data on initial load or token change
  useEffect(() => {
    const verifyUserSession = async () => {
      if (token && !currentUser) { // If token exists but no user data, try to fetch user
        setLoading(true);
        try {
          // Call your backend's /users/me endpoint
          const response = await fetch('/api/v1/users/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const userData = await response.json();
            setCurrentUser(userData);
            // console.log("[AuthContext] User session verified:", userData);
          } else {
            // Token might be invalid or expired
            console.warn("[AuthContext] Token verification failed or session expired.");
            logout(); // Clear invalid token
          }
        } catch (error) {
          console.error("[AuthContext] Error verifying user session:", error);
          logout(); // Clear token on error
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false); // No token, or user already loaded
      }
    };

    verifyUserSession();
  }, [token, currentUser]); // Rerun when token or currentUser changes

  const value = {
    currentUser,
    token,
    loading,
    login,
    logout,
  };

  // Don't render children until loading is false to prevent flicker or premature route access
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}